import type { ReactNode } from "react";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export type ProjectFinancialSummaryShape = {
  takhmeen: {
    pledgeCount?: number;
    totalCommitted: number;
    totalPaid: number;
    totalPending: number;
  };
  inayat: {
    entryCount?: number;
    totalInayat: number;
  };
  payments: {
    paymentCount?: number;
    totalSpent: number;
  };
  karzan: {
    loanCount?: number;
    karzanTotalPrincipal: number;
    karzanTotalRepaid: number;
    karzanTotalOutstanding: number;
    karzans?: Array<{
      id: string;
      lenderName?: string | null;
      principalAmount: number;
      repaidAmount: number;
      outstanding: number;
    }>;
  };
  checks: {
    fundingFromGiftsAndCollections: number;
    totalCollected: number;
    totalSpent: number;
    collectedMinusSpent: number;
    estimatedBudget: number;
    karzanOutstanding: number;
    coverageHint: number;
  };
};

const fmt = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);

const fmtShort = (value: number) => {
  const v = Math.abs(value);
  if (v >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(value / 1000).toFixed(0)}K`;
  return `₹${value}`;
};

const CHART_H = 260;

type Props = {
  summary: ProjectFinancialSummaryShape;
};

const ChartCard = ({
  title,
  subtitle,
  children,
  empty,
}: {
  title: string;
  subtitle?: string;
  children?: ReactNode;
  empty?: boolean;
}) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ "&:last-child": { pb: 2 } }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1, mt: 0.25 }}
        >
          {subtitle}
        </Typography>
      ) : (
        <Box sx={{ mb: 1 }} />
      )}
      {empty ? (
        <Typography variant="caption" color="text.secondary">
          Nothing to show yet.
        </Typography>
      ) : (
        children
      )}
    </CardContent>
  </Card>
);

/** Compare budget, takhmeen, inayat, karzan, and work spend on one scale. */
const KeyAmountsBar = ({ summary }: Props) => {
  const theme = useTheme();
  const budget = Math.max(0, summary.checks.estimatedBudget);
  const paid = Math.max(0, summary.takhmeen.totalPaid);
  const pending = Math.max(0, summary.takhmeen.totalPending);
  const inayat = Math.max(0, summary.inayat.totalInayat);
  const karzanOut = Math.max(0, summary.karzan.karzanTotalOutstanding);
  const spent = Math.max(0, summary.checks.totalSpent);

  const data = [
    { name: "Budget", value: budget, fill: theme.palette.primary.main },
    { name: "Takhmeen collected", value: paid, fill: theme.palette.success.main },
    { name: "Takhmeen pending", value: pending, fill: theme.palette.warning.main },
    { name: "Inayat", value: inayat, fill: theme.palette.info.main },
    { name: "Work spent", value: spent, fill: theme.palette.grey[700] },
    { name: "Karzan owed", value: karzanOut, fill: theme.palette.error.light },
  ];

  const maxVal = Math.max(0, ...data.map((d) => d.value));
  const empty = maxVal <= 0;

  return (
    <ChartCard
      title="Key amounts"
      subtitle="Budget, takhmeen, inayat, work payments, and karzan liability side by side."
      empty={empty}
    >
      <ResponsiveContainer width="100%" height={CHART_H}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 56 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10 }}
            interval={0}
            angle={-28}
            textAnchor="end"
            height={56}
          />
          <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={56} />
          <Tooltip formatter={(v) => fmt(Number(v))} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
            {data.map((d) => (
              <Cell key={d.name} fill={d.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

/** Where cash-in came from: takhmeen receipts vs inayat. */
const CashInMixPie = ({ summary }: Props) => {
  const theme = useTheme();
  const paid = Math.max(0, summary.takhmeen.totalPaid);
  const inayat = Math.max(0, summary.inayat.totalInayat);
  const data = [
    { name: "Takhmeen collected", value: paid, color: theme.palette.success.main },
    { name: "Inayat", value: inayat, color: theme.palette.info.main },
  ].filter((d) => d.value > 0);

  if (paid + inayat <= 0) {
    return <ChartCard title="Cash-in mix" subtitle="Takhmeen receipts vs inayat." empty />;
  }

  return (
    <ChartCard title="Cash-in mix" subtitle="Takhmeen receipts vs inayat.">
      <ResponsiveContainer width="100%" height={CHART_H}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={52}
            outerRadius={88}
            paddingAngle={2}
          >
            {data.map((entry, idx) => (
              <Cell key={idx} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => fmt(Number(value))} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const TakhmeenCollectedPendingPie = ({ summary }: Props) => {
  const theme = useTheme();
  const paid = Math.max(0, summary.takhmeen.totalPaid);
  const pending = Math.max(0, summary.takhmeen.totalPending);
  const data = [
    { name: "Collected", value: paid, color: theme.palette.success.main },
    { name: "Pending", value: pending, color: theme.palette.warning.main },
  ].filter((d) => d.value > 0);

  if (paid + pending <= 0) {
    return (
      <ChartCard title="Takhmeen: collected vs pending" subtitle="Against pledged amounts." empty />
    );
  }

  return (
    <ChartCard title="Takhmeen: collected vs pending" subtitle="Against pledged amounts.">
      <ResponsiveContainer width="100%" height={CHART_H}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={52}
            outerRadius={88}
            paddingAngle={2}
          >
            {data.map((entry, idx) => (
              <Cell key={idx} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => fmt(Number(value))} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

/** Committed vs collected vs still pending on pledges. */
const TakhmeenPledgesBar = ({ summary }: Props) => {
  const theme = useTheme();
  const c = Math.max(0, summary.takhmeen.totalCommitted);
  const paid = Math.max(0, summary.takhmeen.totalPaid);
  const pending = Math.max(0, summary.takhmeen.totalPending);
  const data = [{ name: "Takhmeen", Committed: c, Collected: paid, Pending: pending }];
  const empty = c + paid + pending <= 0;

  return (
    <ChartCard
      title="Takhmeen pledges"
      subtitle={`${summary.takhmeen.pledgeCount ?? 0} pledge(s) · committed, collected, pending.`}
      empty={empty}
    >
      <ResponsiveContainer width="100%" height={CHART_H}>
        <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={false} height={8} />
          <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={56} />
          <Tooltip formatter={(v) => fmt(Number(v))} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="Committed" fill={theme.palette.primary.light} radius={[4, 4, 0, 0]} />
          <Bar dataKey="Collected" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
          <Bar dataKey="Pending" fill={theme.palette.warning.main} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const KarzanRepaidOutstandingPie = ({ summary }: Props) => {
  const theme = useTheme();
  const repaid = Math.max(0, summary.karzan.karzanTotalRepaid);
  const out = Math.max(0, summary.karzan.karzanTotalOutstanding);
  const data = [
    { name: "Repaid", value: repaid, color: theme.palette.success.main },
    { name: "Outstanding", value: out, color: theme.palette.error.light },
  ].filter((d) => d.value > 0);

  if (repaid + out <= 0) {
    return <ChartCard title="Karzan: repaid vs outstanding" subtitle="Across all loans." empty />;
  }

  return (
    <ChartCard title="Karzan: repaid vs outstanding" subtitle="Across all loans.">
      <ResponsiveContainer width="100%" height={CHART_H}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={52}
            outerRadius={88}
            paddingAngle={2}
          >
            {data.map((entry, idx) => (
              <Cell key={idx} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => fmt(Number(value))} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const KarzanPrincipalBar = ({ summary }: Props) => {
  const theme = useTheme();
  const principal = Math.max(0, summary.karzan.karzanTotalPrincipal);
  const repaid = Math.max(0, summary.karzan.karzanTotalRepaid);
  const out = Math.max(0, summary.karzan.karzanTotalOutstanding);
  const data = [{ name: "Karzan", Principal: principal, Repaid: repaid, Outstanding: out }];
  const empty = principal + repaid + out <= 0;

  return (
    <ChartCard
      title="Karzan totals"
      subtitle={`${summary.karzan.loanCount ?? 0} loan(s) · principal, repaid, outstanding.`}
      empty={empty}
    >
      <ResponsiveContainer width="100%" height={CHART_H}>
        <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={false} height={8} />
          <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={56} />
          <Tooltip formatter={(v) => fmt(Number(v))} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="Principal" fill={theme.palette.grey[400]} radius={[4, 4, 0, 0]} />
          <Bar dataKey="Repaid" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
          <Bar dataKey="Outstanding" fill={theme.palette.error.light} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const BudgetCollectedSpentBar = ({ summary }: Props) => {
  const theme = useTheme();
  const budget = Math.max(0, summary.checks.estimatedBudget);
  const collected = Math.max(0, summary.checks.totalCollected);
  const spent = Math.max(0, summary.checks.totalSpent);
  const data = [
    { name: "Budget", value: budget, fill: theme.palette.primary.main },
    { name: "Collected", value: collected, fill: theme.palette.success.main },
    { name: "Work spent", value: spent, fill: theme.palette.warning.dark },
  ];
  const empty = budget <= 0 && collected <= 0 && spent <= 0;

  return (
    <ChartCard
      title="Budget vs collected vs spent"
      subtitle="Planned budget, money in (takhmeen + Inayat), and work payments out."
      empty={empty}
    >
      <ResponsiveContainer width="100%" height={CHART_H}>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={fmtShort} tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => fmt(Number(v))} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {data.map((d) => (
              <Cell key={d.name} fill={d.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

const KarzanLoansBar = ({ summary }: Props) => {
  const theme = useTheme();
  const rows = summary.karzan.karzans ?? [];
  if (rows.length === 0) {
    return <ChartCard title="Karzan by lender" subtitle="Repaid vs outstanding per loan." empty />;
  }

  const data = rows.map((row) => ({
    name:
      (row.lenderName ?? "Lender").length > 16
        ? `${(row.lenderName ?? "Lender").slice(0, 14)}…`
        : (row.lenderName ?? "Lender"),
    Repaid: Math.max(0, row.repaidAmount),
    Outstanding: Math.max(0, row.outstanding),
  }));

  return (
    <ChartCard title="Karzan by lender" subtitle="Repaid vs outstanding per loan.">
      <ResponsiveContainer width="100%" height={Math.min(380, 120 + data.length * 36)}>
        <BarChart data={data} layout="vertical" margin={{ left: 4, right: 12, top: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" tickFormatter={fmtShort} tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(value) => fmt(Number(value))} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="Repaid" stackId="a" fill={theme.palette.success.main} />
          <Bar dataKey="Outstanding" stackId="a" fill={theme.palette.error.light} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export const ProjectShowProgressCharts = ({ summary }: Props) => (
  <Grid container spacing={2}>
    <Grid size={{ xs: 12, lg: 7 }}>
      <KeyAmountsBar summary={summary} />
    </Grid>
    <Grid size={{ xs: 12, lg: 5 }}>
      <BudgetCollectedSpentBar summary={summary} />
    </Grid>
    <Grid size={{ xs: 12, md: 6, lg: 4 }}>
      <CashInMixPie summary={summary} />
    </Grid>
    <Grid size={{ xs: 12, md: 6, lg: 4 }}>
      <TakhmeenCollectedPendingPie summary={summary} />
    </Grid>
    <Grid size={{ xs: 12, md: 6, lg: 4 }}>
      <KarzanRepaidOutstandingPie summary={summary} />
    </Grid>
    <Grid size={{ xs: 12, md: 6 }}>
      <TakhmeenPledgesBar summary={summary} />
    </Grid>
    <Grid size={{ xs: 12, md: 6 }}>
      <KarzanPrincipalBar summary={summary} />
    </Grid>
    <Grid size={{ xs: 12 }}>
      <KarzanLoansBar summary={summary} />
    </Grid>
  </Grid>
);
